/**
 * GymContext — provides the active gym ID, gym-scoped Firestore helpers,
 * gym meta document state, the caller's own member document, and the slug
 * resolver.
 *
 * The active gym comes from the URL alone (`/gym/:gymSlug/…`): the provider
 * reads the slug itself and resolves it. Nothing pushes a gym id in from
 * below — GymShell used to call `setGymId` during its own render, an
 * ancestor update React reports as "Cannot update a component while
 * rendering a different component", which also reached the children one
 * render late.
 */
import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from "react";
import { useMatch } from "react-router";
import { db } from "../firebase";
import { useCurrentUser } from "./CurrentUserContext";
import { useListenerRetry } from "../hooks/useListenerRetry";
import { sessionStore } from "../utils/safeStorage";
import { doc, collection, onSnapshot, query, where, getDocs, limit, disableNetwork, enableNetwork } from "firebase/firestore";

const GymContext = createContext(null);

// ── Slug-resolve resilience ──
//
// Firestore's one-shot reads carry NO client-side deadline. If the very first
// stream handshake of a cold boot wedges — a tab/PWA that iOS suspended hands
// the SDK a connection the OS already tore down — `getDocs` never resolves AND
// never rejects. GymShell then sits on <LoadingBlank> forever: the
// "boot-stall: gym-resolve" white screen the boot watchdog now records. Nothing
// recovers it on its own, because unlike the `onSnapshot` listeners (which
// useGymData re-attaches on error/timeout) a one-shot read fires exactly once.
//
// Crash-log snapshots of real stalls show `online: true` with the font / GA /
// firebase-config requests all COMPLETING while no firestore.googleapis.com
// request ever finishes — i.e. the network is alive and only the Firestore
// channel is wedged. So the fix is to impose our own deadline and, on expiry,
// make the SDK tear that channel down and rebuild it before retrying.
const RESOLVE_TIMEOUT_MS = 6000;
const RESOLVE_MAX_ATTEMPTS = 3;

// Test seam: lets the unit tests exercise the stall/retry path without waiting
// out the real deadline three times over. Mirrors the `__*ForTests` helpers in
// utils/serverTime.js. Never called by app code.
let resolveTimeoutMs = RESOLVE_TIMEOUT_MS;
export function __setResolveTimeoutForTests(ms) {
  resolveTimeoutMs = typeof ms === "number" ? ms : RESOLVE_TIMEOUT_MS;
}

// Distinct from any value getDocs can resolve with, so a timeout is never
// mistaken for an empty result (which would read as "gym not found").
const TIMED_OUT = Symbol("timed-out");

function withDeadline(promise, ms) {
  let timer;
  return Promise.race([
    promise,
    new Promise((resolve) => { timer = setTimeout(() => resolve(TIMED_OUT), ms); }),
  ]).finally(() => clearTimeout(timer));
}

// Force the SDK to drop and rebuild its backend channel. This is the documented
// remedy for a wedged connection and the only lever we have from the client —
// the SDK exposes no per-read timeout or "reconnect now" call.
//
// It is instance-wide: any live listener briefly goes offline (memory-only
// cache = no data until re-enabled). That blip is acceptable because we only
// ever kick when a read is ALREADY wedged — a sub-second interruption strictly
// beats an indefinite hang — and at the gym-resolve stage of a cold boot there
// are barely any listeners attached yet. Shared in-flight promise so concurrent
// callers trigger exactly one rebuild.
let kickInFlight = null;
function rebuildFirestoreChannel() {
  if (kickInFlight) return kickInFlight;
  kickInFlight = (async () => {
    try {
      await disableNetwork(db);
      await enableNetwork(db);
    } catch (_) {
      // Best effort — a failed kick just means the retry runs on the old
      // channel, which is no worse than not trying.
    }
  })();
  // Release the guard once settled so a later stall can kick again.
  kickInFlight.finally(() => { kickInFlight = null; });
  return kickInFlight;
}

const SLUG_CACHE_PREFIX = "slug_";

function readSlugCache(slug) {
  return sessionStore.get(SLUG_CACHE_PREFIX + slug);
}

// Resolve a slug to a gymId — returns the gymId, or null when no gym has that
// slug. Access control for inactive gyms is handled in MainApp. Uses a
// sessionStorage cache to avoid repeated Firestore queries on re-navigation.
//
// Deadline-guarded and retried (see the notes at the top of this file): a
// wedged Firestore channel would otherwise hang forever and white-screen the
// gym shell. THROWS `code: "slug-resolve-unavailable"` once the attempts are
// spent — callers must distinguish that from a `null` (genuinely no such gym)
// so a transport failure is never reported to the user as "gym not found".
async function resolveSlug(slug) {
  const cached = readSlugCache(slug);
  if (cached) return cached;
  const q = query(collection(db, "gyms"), where("slug", "==", slug), limit(1));

  let lastFailure = null;
  for (let attempt = 1; attempt <= RESOLVE_MAX_ATTEMPTS; attempt++) {
    let outcome = TIMED_OUT;
    try {
      outcome = await withDeadline(getDocs(q), resolveTimeoutMs);
    } catch (e) {
      // A denial is a real answer, not a transport hiccup — no amount of
      // retrying or channel-rebuilding turns it into a result.
      if (e?.code === "permission-denied") throw e;
      lastFailure = e;
    }
    if (outcome !== TIMED_OUT) {
      const id = outcome.empty ? null : outcome.docs[0].id;
      if (id) sessionStore.set(SLUG_CACHE_PREFIX + slug, id);
      return id;
    }
    if (attempt < RESOLVE_MAX_ATTEMPTS) {
      console.warn(`[gym] slug resolve stalled (attempt ${attempt}/${RESOLVE_MAX_ATTEMPTS}) — rebuilding the Firestore channel`, lastFailure || "");
      await rebuildFirestoreChannel();
    }
  }
  const err = new Error(`Gym slug resolve unavailable after ${RESOLVE_MAX_ATTEMPTS} attempts`);
  err.code = "slug-resolve-unavailable";
  if (lastFailure) err.cause = lastFailure;
  throw err;
}

export function GymProvider({ children }) {
  // ── The active gym, from the URL ──
  //
  // Outside a gym route there is no active gym: the selector, the profile,
  // the calendar and the platform admin neither listen to a gym nor see the
  // id of the one the user visited last. (Starting from the legacy "default
  // gym = project id" would have them surface the demo gym instead.)
  const slugMatch = useMatch("/gym/:gymSlug/*");
  const slug = slugMatch?.params.gymSlug || null;
  // A cached slug gives the id in THIS render, so the gym's children mount
  // with it on their first render — no loading frame on re-navigation.
  const cachedId = slug ? readSlugCache(slug) : null;
  const [resolution, setResolution] = useState({ slug: null, gymId: null, status: "loading" });
  const [resolveNonce, setResolveNonce] = useState(0);
  const ownResolution = resolution.slug === slug ? resolution : null;
  const gymId = !slug
    ? null
    : (cachedId || (ownResolution?.status === "resolved" ? ownResolution.gymId : null));
  // "none" | "loading" | "resolved" | "notFound" | "unavailable"
  const slugStatus = !slug ? "none" : (gymId ? "resolved" : (ownResolution?.status || "loading"));

  useEffect(() => {
    // Leaving the gym routes forgets the last answer, so coming back to a slug
    // that was "notFound" / "unavailable" resolves afresh instead of painting
    // the old screen for a frame.
    if (!slug) {
      setResolution((r) => (r.slug === null ? r : { slug: null, gymId: null, status: "loading" }));
      return;
    }
    if (cachedId) return;
    let cancelled = false;
    setResolution({ slug, gymId: null, status: "loading" });
    resolveSlug(slug).then((id) => {
      if (cancelled) return;
      setResolution(id
        ? { slug, gymId: id, status: "resolved" }
        : { slug, gymId: null, status: "notFound" });
    }).catch((e) => {
      if (cancelled) return;
      // MUST stay handled: an unhandled rejection here would leave the shell on
      // <LoadingBlank> forever — the exact white screen this whole path fixes.
      console.error("[gym] slug resolve failed:", e);
      setResolution({ slug, gymId: null, status: "unavailable" });
    });
    return () => { cancelled = true; };
  }, [slug, cachedId, resolveNonce]);

  const retrySlugResolve = useCallback(() => setResolveNonce((n) => n + 1), []);

  // ── The gym parent doc ──
  //
  // One slot, keyed by gym id, that outlives leaving the gym: when the same
  // gym comes back (gym → profile → gym) its header, accent and feature tabs
  // show at once while the listener re-subscribes. Another gym starts from
  // "loading" — never from the previous gym's document.
  const [gymMetaSlot, setGymMeta] = useState({ gymId: null, meta: null });

  useEffect(() => {
    if (!gymId) return;
    // Same guard as the member listener below: memory-only Firestore emits a
    // transient EMPTY snapshot from cache before the server answers. Without
    // the guard that frame set `gymMeta` to null on every cold boot — the
    // header fell back to "Sendly", every `isFeatureEnabled` gate went false
    // so the Competitions / Events tabs vanished and came back, the accent
    // reset, and `GymProfileEditor` hit a hook-order throw.
    const unsub = onSnapshot(
      doc(db, "gyms", gymId),
      { includeMetadataChanges: true },
      (snap) => {
        if (snap.metadata?.fromCache && !snap.exists()) return;
        setGymMeta({ gymId, meta: snap.exists() ? { id: snap.id, ...snap.data() } : null });
      },
    );
    return unsub;
  }, [gymId]);

  const gymMeta = gymId && gymMetaSlot.gymId === gymId ? gymMetaSlot.meta : null;
  const gymMetaLoading = !!gymId && gymMetaSlot.gymId !== gymId;

  // ── The caller's own member doc ──
  //
  // Four listeners used to watch this one document on a gym page: one in
  // `useGymData` (for the ban flag) and one per `usePermissions` caller —
  // `useMainApp`, `AdminPanel`, `CompetitionTab`. They also disagreed. Only
  // the `useGymRole` copy skipped the transient EMPTY cache snapshot that
  // memory-only Firestore emits before the server answers, so the ban flag
  // and the capability set resolved on different timelines from the same
  // document — and an admin could briefly render as a plain user.
  //
  // `ownMember`: `undefined` means not loaded yet; `null` means loaded and
  // absent (not a member). Consumers MUST keep telling those apart: treating
  // `undefined` as `null` is what flashes the BannedScreen at a member who is
  // fine. It is OPTIMISTIC — it shows the client's own pending write at once.
  //
  // `memberState` is the membership the SERVER has confirmed: "loading" |
  // "active" (a member doc, not banned) | "none". It moves only on a snapshot
  // straight from the server (no pending writes, not from cache). On a first
  // join useAuth's `setDoc` fires a local snapshot ~0.5 s before the server
  // has the doc; a rules-gated listener (`isActiveGymMember`) attached on that
  // local snapshot was denied, and a denied listener is not reattached. Every
  // member-gated listener keys on this, never on `ownMember`'s presence.
  //
  // Same one-slot memory as the gym doc above, keyed by gym AND user — but
  // only for `ownMember` (role, caps, ban flag for the UI). `memberState`
  // goes back to "loading" when the gym or the user changes (leaving the gym
  // included) and waits for the server again: a membership confirmed before
  // the user left the gym may have ended (left, removed) while they were away.
  // A retry of the listener keeps it — that would tear down every gated
  // listener for nothing, and a last failed retry would leave them off.
  const { authUser } = useCurrentUser();
  const memberUid = authUser?.uid || null;
  const memberKey = gymId && memberUid ? `${gymId}|${memberUid}` : null;
  const [memberSlot, setMemberSlot] = useState({ key: null, member: undefined, state: "loading" });
  const { nonce: memberNonce, onError: onMemberError, markAlive: memberAlive } =
    useListenerRetry("gymMember", memberKey || "");

  useEffect(() => {
    if (!gymId || !memberUid) return;
    const key = `${gymId}|${memberUid}`;
    const unsub = onSnapshot(
      doc(db, "gyms", gymId, "members", memberUid),
      { includeMetadataChanges: true },
      (snap) => {
        if (snap.metadata.fromCache && !snap.exists()) return;
        memberAlive();
        const member = snap.exists() ? { uid: snap.id, ...snap.data() } : null;
        setMemberSlot((prev) => {
          const prevState = prev.key === key ? prev.state : "loading";
          const state = snap.metadata.hasPendingWrites || snap.metadata.fromCache
            ? prevState
            : (member && member.banned !== true ? "active" : "none");
          return { key, member, state };
        });
      },
      onMemberError("ownMember"),
    );
    return unsub;
  }, [gymId, memberUid, memberNonce, onMemberError, memberAlive]);

  // The reset, in the cleanup of an effect keyed by gym + user only (so a
  // listener retry does not run it). It lands when the user LEAVES, so on
  // gym → profile → the same gym the first render after the return already
  // says "loading": children's effects run before this provider's, and a reset
  // at the next subscribe would come after they attached on a stale "active".
  useEffect(() => {
    if (!memberKey) return undefined;
    const key = memberKey;
    return () => setMemberSlot((prev) => (prev.key === key && prev.state !== "loading" ? { ...prev, state: "loading" } : prev));
  }, [memberKey]);

  const ownSlot = memberKey && memberSlot.key === memberKey ? memberSlot : null;
  const ownMember = ownSlot ? ownSlot.member : undefined;
  const memberState = ownSlot ? ownSlot.state : "loading";

  // Null until a gym is active — doc() throws on a null path segment, and
  // gym-scoped consumers only mount under /gym/:slug (gymId already resolved).
  const gymDocRef = useMemo(() => (gymId ? doc(db, "gyms", gymId) : null), [gymId]);

  const gymCollection = useCallback(
    (subcollection) => collection(db, "gyms", gymId, subcollection),
    [gymId]
  );

  const gymDoc = useCallback(
    (subcollection, docId) => doc(db, "gyms", gymId, subcollection, docId),
    [gymId]
  );

  const value = useMemo(() => ({
    gymId, slugStatus, retrySlugResolve,
    gymMeta, gymMetaLoading,
    ownMember, memberState,
    gymDocRef,
    gymCollection, gymDoc,
    resolveSlug,
  }), [gymId, slugStatus, retrySlugResolve, gymMeta, gymMetaLoading, ownMember, memberState, gymDocRef, gymCollection, gymDoc]);

  return (
    <GymContext.Provider value={value}>
      {children}
    </GymContext.Provider>
  );
}

export function useGym() {
  const ctx = useContext(GymContext);
  if (!ctx) throw new Error("useGym must be used within GymProvider");
  return ctx;
}

/**
 * Same context, but null outside a GymProvider instead of throwing.
 *
 * For shared components that normally render inside a gym but must not crash
 * if they're ever mounted elsewhere — they degrade instead. Use `useGym()`
 * whenever the gym context is genuinely required.
 */
export function useGymOptional() {
  return useContext(GymContext);
}
